import {
  CabinContentsCoverage,
  CabinCoverage,
} from "Store/interfaces/products/coverage";
import {
  formBooleanToBoolean,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";
import {
  filterCoveragesOnCoverage,
  getMonthlyPrice,
} from "../../houseProductsUtils";

import { CheckPriceProps } from "Components/Features/CheckPrice";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { getYearlyDiscountValue } from "Features/CheckPrice/Products/RiskProducts/riskProductsUtils";
import { CoverageToAdd } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { isDefined } from "Utils/functions";
import { CabinProductDetailsFormValues } from "../ProductDetails/cabinProductDetailsInterfaces";
import { CabinProductIntroFormValues } from "../ProductIntro/cabinProductIntroInterfaces";
import { tooOldForSuper } from "../cabinConditions";
import { NotSuperReasonCabin } from "../cabinTypes";
import { CabinPriceFormValues } from "./cabinProductPriceInterfaces";

export const findNotSuperReason = (
  formValuesIntro?: CabinProductIntroFormValues,
  formValuesDetails?: CabinProductDetailsFormValues,
): NotSuperReasonCabin | null => {
  if (
    isDefined(formValuesIntro) &&
    tooOldForSuper(parseInt(formValuesIntro.constructionYear))
  ) {
    return NotSuperReasonCabin.TOO_OLD_CABIN;
  }
  if (isDefined(formValuesDetails)) {
    if (formBooleanToBoolean(formValuesDetails.pestsDamaged)) {
      return NotSuperReasonCabin.HAS_DAMAGE_FROM_PESTS;
    }
    if (formBooleanToBoolean(formValuesDetails.hasCrawlspace)) {
      return NotSuperReasonCabin.HAS_CRAWL_SPACE;
    }
  }
  return null;
};

export const getInitialFormStateWithCorrectCoverage = (
  formState: Partial<CabinPriceFormValues>,
  standardOnly: boolean,
): Partial<CabinPriceFormValues> => {
  const coverage = standardOnly
    ? CabinCoverage.CABIN_STANDARD
    : formState.coverage;

  return {
    ...formState,
    coverage: coverage,
    contentsCoverage: CabinContentsCoverage.CABIN_CONTENTS_EXTENDED,
  };
};

export const getTotalPricePerMonth = (
  formValues: CabinPriceFormValues,
  coverages: CoveragePriceCalculation[],
): number => {
  const monthlyCabinPrice: number = getMonthlyPrice(
    formValues.deductible,
    formValues.coverage,
    filterCoveragesOnCoverage(Object.values(CabinCoverage), coverages),
  );

  if (
    formBooleanToBoolean(formValues.addContentsInsurance) &&
    formValues.contentsDeductible &&
    formValues.contentsCoverage
  ) {
    const monthlyCabinContentsPrice = getMonthlyPrice(
      formValues.contentsDeductible,
      formValues.contentsCoverage,
      filterCoveragesOnCoverage(
        Object.values(CabinContentsCoverage),
        coverages,
      ),
    );
    return monthlyCabinPrice + monthlyCabinContentsPrice;
  }
  return monthlyCabinPrice;
};

export const calculateTotalDiscountValuePerYear = (
  formValues: CabinPriceFormValues,
): number => {
  const coverages = useCoveragePriceCalculations();
  const cabinDiscountValue =
    getYearlyDiscountValue(
      formValues.deductible,
      [formValues.coverage],
      coverages,
    ) ?? 0;

  if (
    formBooleanToBoolean(formValues.addContentsInsurance) &&
    formValues.contentsDeductible &&
    formValues.contentsCoverage
  ) {
    const cabinContentsDiscountValue =
      getYearlyDiscountValue(
        formValues.contentsDeductible,
        [formValues.contentsCoverage],
        coverages,
      ) ?? 0;
    return cabinDiscountValue + cabinContentsDiscountValue;
  }
  return cabinDiscountValue;
};

export const sendCabinContentsPriceRequest = (
  cabinProductIntroFormValues: CabinProductIntroFormValues,
  cabinProductDetailsFormValues: CabinProductDetailsFormValues,
  cabinProductPriceFormValues: CabinPriceFormValues,
  sendRelatedPriceRequest: CheckPriceProps["sendRelatedPriceRequest"],
  contentsInsuranceSum?: number,
): void => {
  sendRelatedPriceRequest &&
    sendRelatedPriceRequest(BackendProductInfo.CABIN_CONTENTS.priceReqUrl, {
      injuryYear: cabinProductPriceFormValues.injuryYear,
      injuries: Number.isNaN(cabinProductPriceFormValues.injuries)
        ? undefined
        : Number(cabinProductPriceFormValues.injuries),
      postNumber: cabinProductIntroFormValues.postNumber,
      hasTapWater:
        nullableFormBooleanToNullableBoolean(
          cabinProductDetailsFormValues.hasTapWater,
        ) ?? false,
      hasElectricity:
        nullableFormBooleanToNullableBoolean(
          cabinProductDetailsFormValues.hasElectricity,
        ) ?? false,
      contentAmount: contentsInsuranceSum,
    });
};

export const getProductsToAddToCart = (
  formProps: CabinPriceFormValues,
): CoverageToAdd[] => {
  const productsToAdd = [];
  productsToAdd.push({
    coverage: formProps.coverage,
    productType: INSURANCE_NAME_VALUE.CABIN,
    filterValue: formProps.deductible,
  });
  if (
    formBooleanToBoolean(formProps.addContentsInsurance) &&
    formProps.contentsCoverage
  ) {
    productsToAdd.push({
      coverage: formProps.contentsCoverage,
      productType: INSURANCE_NAME_VALUE.CABIN_CONTENTS,
      filterValue: formProps.contentsDeductible,
    });
  }
  return productsToAdd;
};
